import { useMemo, useState } from "react";
import { Alert, Button, Chip, Divider, Input, Spinner } from "@heroui/react";
import {
    faChevronLeft,
    faSearch,
    faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import CspResourceForm from "@/components/csp-resources/CspResourceForm";
import CspResourceTable from "@/components/csp-resources/CspResourceTable";
import TableV2 from "@/components/table/TableV2";
import useAuth from "@/lib/auth/useAuth";
import useRequest from "@/lib/request/useRequest";
import { CspResourceCsp, CspResourceRecipients } from "@/types/cspResource";

type CspViewSearch = { organization?: string };

export const Route = createFileRoute("/dashboard/resources/csp-view")({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown>): CspViewSearch => {
        const value = search.organization;
        const organization =
            typeof value === "string" || typeof value === "number"
                ? String(value)
                : "";
        return {
            organization:
                /^[1-9]\d*$/.test(organization) &&
                Number.isSafeInteger(Number(organization))
                    ? organization
                    : undefined,
        };
    },
});

function RouteComponent() {
    const { organization } = Route.useSearch();
    const navigate = Route.useNavigate();
    const { userIsSentinel } = useAuth();
    const { useGetRequest } = useRequest();
    const choices = useGetRequest<CspResourceRecipients>({
        url: "/csp_resources/recipients/",
        enabled: userIsSentinel,
    });
    const selected = choices.data?.csps.find(
        (csp) => String(csp.id) === organization,
    );
    const [search, setSearch] = useState("");
    const [uploadCsp, setUploadCsp] = useState<CspResourceCsp | null>(null);
    const [sorting, setSorting] = useState<SortingState>([
        { id: "name", desc: false },
    ]);
    const visibleCsps = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        return (choices.data?.csps ?? []).filter((csp) =>
            `${csp.name} ${csp.id}`.toLowerCase().includes(normalizedSearch),
        );
    }, [choices.data?.csps, search]);
    const columns = useMemo<ColumnDef<CspResourceRecipients["csps"][number]>[]>(
        () => [
            {
                accessorKey: "name",
                header: "CSP",
                size: 360,
                cell: ({ row }) => (
                    <div className="flex min-w-0 flex-col">
                        <Link
                            to="/dashboard/resources/csp-view"
                            search={{ organization: String(row.original.id) }}
                            onClick={(event) => event.stopPropagation()}
                            className="truncate font-medium text-primary-500 hover:underline"
                            title={row.original.name}
                        >
                            {row.original.name}
                        </Link>
                        <span className="text-xs text-gray-500">
                            ID: {row.original.id}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: "csp_tier",
                header: "CSP tier",
                size: 170,
                cell: ({ row }) => (
                    <Chip
                        size="sm"
                        variant="flat"
                        radius="sm"
                        color={row.original.csp_tier ? "primary" : "default"}
                    >
                        {choices.data?.tiers.find(
                            (tier) => tier.id === row.original.csp_tier,
                        )?.description ?? "Unassigned"}
                    </Chip>
                ),
            },
            {
                accessorKey: "resource_count",
                header: "Resources",
                size: 120,
                cell: ({ row }) => (
                    <span className="font-medium tabular-nums">
                        {row.original.resource_count}
                    </span>
                ),
            },
            {
                id: "actions",
                header: "Actions",
                size: 140,
                enableSorting: false,
                cell: ({ row }) => (
                    <div onClick={(event) => event.stopPropagation()}>
                        <Button
                            size="sm"
                            variant="flat"
                            startContent={<FontAwesomeIcon icon={faUpload} />}
                            aria-label={`Upload for ${row.original.name}`}
                            onPress={() => setUploadCsp(row.original)}
                        >
                            Upload
                        </Button>
                    </div>
                ),
            },
        ],
        [choices.data?.tiers],
    );

    if (!userIsSentinel) return null;

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
            <div
                className={
                    organization
                        ? "flex w-full flex-wrap items-center gap-2 p-3"
                        : "flex flex-col gap-3 p-3"
                }
            >
                {organization ? (
                    <Button
                        variant="light"
                        startContent={<FontAwesomeIcon icon={faChevronLeft} />}
                        onPress={() =>
                            navigate({ search: { organization: undefined } })
                        }
                    >
                        Back to CSPs
                    </Button>
                ) : (
                    <>
                        <div>
                            <h2 className="text-xl font-bold text-primary-500">
                                CSPs
                            </h2>
                        </div>
                        <Input
                            aria-label="Search CSPs"
                            placeholder="Search CSPs by name or ID…"
                            value={search}
                            onValueChange={setSearch}
                            isClearable
                            onClear={() => setSearch("")}
                            startContent={<FontAwesomeIcon icon={faSearch} />}
                            size="md"
                            className="w-full sm:max-w-md"
                        />
                    </>
                )}
            </div>
            <Divider />
            <div className="min-h-0 flex-1">
                {choices.isPending ? (
                    <div className="flex h-full min-h-0 items-center justify-center p-6">
                        <Spinner label="Loading CSPs" />
                    </div>
                ) : choices.isError ? (
                    <div className="p-3">
                        <Alert
                            color="danger"
                            title="We couldn’t load CSPs."
                            endContent={
                                <Button
                                    size="sm"
                                    variant="flat"
                                    color="primary"
                                    isLoading={choices.isFetching}
                                    onPress={() => choices.refetch()}
                                >
                                    Try again
                                </Button>
                            }
                        />
                    </div>
                ) : selected ? (
                    <CspResourceTable key={selected.id} scope={selected} />
                ) : organization ? (
                    <p className="p-6 text-sm text-gray-500">
                        This CSP is unavailable. Select another CSP to view its
                        resources.
                    </p>
                ) : (
                    <div className="h-full p-3">
                        <TableV2
                            data={visibleCsps}
                            columns={columns}
                            sorting={sorting}
                            setSorting={setSorting}
                            onRowClick={(csp) =>
                                navigate({
                                    search: { organization: String(csp.id) },
                                })
                            }
                            estimateSize={48}
                            fillWidthIfPossible
                            showScrollbar
                        />
                    </div>
                )}
            </div>
            {uploadCsp && (
                <CspResourceForm
                    csp={uploadCsp}
                    onClose={() => setUploadCsp(null)}
                />
            )}
        </div>
    );
}
